import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { getCurrencySymbol } from '../utils/currency';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import './Purchases.css';

function Purchases({ tenantId }) {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [formData, setFormData] = useState({
    supplier: '',
    items: [{ name: '', quantity: 1, unit: 'Stück', price: 0 }],
    date: new Date().toISOString().split('T')[0],
    locationId: '',
    notes: ''
  });

  useEffect(() => {
    if (tenantId) {
      loadTenant();
      loadPurchases();
      loadLocations();
    }
  }, [tenantId]);

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadPurchases = async () => {
    try {
      const data = await api.get('/purchases', tenantId);
      setPurchases(data);
    } catch (error) {
      console.error('Error loading purchases:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const data = await api.get('/locations', tenantId);
      setLocations(data);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : '-';
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === 'quantity' || field === 'price') {
      newItems[index] = { ...newItems[index], [field]: value === '' ? '' : value };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setFormData({ ...formData, items: newItems });
  };

  const handleItemBlur = (index, field) => {
    const newItems = [...formData.items];
    if (field === 'quantity' || field === 'price') {
      const val = newItems[index][field] === '' ? (field === 'quantity' ? 1 : 0) : parseFloat(newItems[index][field]) || (field === 'quantity' ? 1 : 0);
      newItems[index] = { ...newItems[index], [field]: val };
      setFormData({ ...formData, items: newItems });
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { name: '', quantity: 1, unit: 'Stück', price: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        items: formData.items.map(item => ({
          ...item,
          quantity: item.quantity === '' ? 1 : parseFloat(item.quantity) || 1,
          price: item.price === '' ? 0 : parseFloat(item.price) || 0
        }))
      };
      const totalAmount = submitData.items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
      
      if (editingPurchase) {
        await api.put(`/purchases/${editingPurchase.id}`, { ...submitData, totalAmount }, tenantId);
      } else {
        await api.post('/purchases', { ...submitData, totalAmount }, tenantId);
      }
      setShowForm(false);
      setEditingPurchase(null);
      setFormData({
        supplier: '',
        items: [{ name: '', quantity: 1, unit: 'Stück', price: 0 }],
        date: new Date().toISOString().split('T')[0],
        locationId: '',
        notes: ''
      });
      loadPurchases();
    } catch (error) {
      console.error('Error saving purchase:', error);
    }
  };

  const handleEdit = (purchase) => {
    setEditingPurchase(purchase);
    setFormData({
      ...purchase,
      date: purchase.date ? purchase.date.split('T')[0] : new Date().toISOString().split('T')[0],
      items: purchase.items && purchase.items.length > 0 ? purchase.items : [{ name: '', quantity: 1, unit: 'Stück', price: 0 }]
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Anschaffung wirklich löschen?')) {
      try {
        await api.delete(`/purchases/${id}`, tenantId);
        loadPurchases();
      } catch (error) {
        console.error('Error deleting purchase:', error);
      }
    }
  };

  const totalSpent = purchases.reduce((sum, p) => sum + (p.totalAmount || 0), 0);

  return (
    <div className="purchases">
      <div className="page-header">
        <h1>Anschaffungen</h1>
        <div>
          <span style={{ marginRight: '1rem', color: '#666' }}>
            Gesamtausgaben: <strong>{totalSpent.toFixed(2)} {currency}</strong>
          </span>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary">
            Neue Anschaffung
          </button>
        </div>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingPurchase ? 'Anschaffung bearbeiten' : 'Neue Anschaffung'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Lieferant</label>
                  <input
                    type="text"
                    value={formData.supplier}
                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Datum</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Werkstattort</label>
                <select
                  value={formData.locationId}
                  onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                >
                  <option value="">Kein Ort</option>
                  {locations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Positionen</label>
                {formData.items.map((item, index) => (
                  <div key={index} className="item-row">
                    <input
                      type="text"
                      placeholder="Name"
                      value={item.name}
                      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                      className="item-name"
                    />
                    <input
                      type="number"
                      placeholder="Menge"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      onBlur={() => handleItemBlur(index, 'quantity')}
                      className="item-quantity"
                      min="1"
                    />
                    <input
                      type="text"
                      placeholder="Einheit"
                      value={item.unit}
                      onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                      className="item-unit"
                    />
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        placeholder={`Preis (${currency})`}
                        value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                        onBlur={() => handleItemBlur(index, 'price')}
                        className="item-price"
                        step="0.01"
                        min="0"
                        style={{ paddingRight: '2.5rem', width: '100%' }}
                      />
                      <span style={{ 
                        position: 'absolute', 
                        right: '0.5rem', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        color: '#666',
                        pointerEvents: 'none',
                        fontSize: '0.9rem'
                      }}>
                        {currency}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="btn-small btn-danger"
                    >
                      Entfernen
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addItem} className="btn-secondary" style={{ marginTop: '0.5rem' }}>
                  Position hinzufügen
                </button>
                <div className="total-amount">
                  <strong>Gesamt: {calculateTotal().toFixed(2)} {currency}</strong>
                </div>
              </div>
              <div className="form-group">
                <label>Notizen</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingPurchase ? 'Speichern' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingPurchase(null);
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DataTable
        data={purchases}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '80px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                />
              </div>
            )
          },
          {
            key: 'date',
            header: 'Datum',
            sortType: 'date',
            accessor: (row) => row.date,
            render: (row) => row.date ? new Date(row.date).toLocaleDateString('de-DE') : ''
          },
          {
            key: 'supplier',
            header: 'Lieferant',
            sortType: 'string',
            accessor: (row) => row.supplier || '',
            render: (row) => (
              <button 
                className="link-button" 
                onClick={() => navigate(`/purchases/${row.id}`)}
              >
                {row.supplier || ''}
              </button>
            )
          },
          {
            key: 'locationId',
            header: 'Werkstattort',
            sortType: 'string',
            accessor: (row) => getLocationName(row.locationId)
          },
          {
            key: 'items',
            header: 'Positionen',
            sortable: false,
            render: (row) => `${row.items?.length || 0} Positionen`
          },
          {
            key: 'totalAmount',
            header: 'Betrag',
            sortType: 'number',
            align: 'right',
            accessor: (row) => row.totalAmount || 0,
            render: (row) => <strong>{row.totalAmount?.toFixed(2)} {currency}</strong>
          }
        ]}
        defaultSort={{ key: 'date', direction: 'desc' }}
        emptyMessage="Keine Anschaffungen vorhanden"
      />
    </div>
  );
}

export default Purchases;

